import type { UserInput } from '@skop-app/lib-domain/types'

import { pref } from '@/utils/pref'
import type {
  OnboardingObjective,
  OnboardingPreferencePayload,
  OnboardingStatus,
} from '@/types/onboarding'

import { MANAGE_ONBOARDING_PREF_KEY } from './constants'

export const parseOnboardingPreference = (
  raw: string | undefined,
): OnboardingPreferencePayload | null => {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as OnboardingPreferencePayload
    if (
      parsed?.status !== 'complete' &&
      parsed?.status !== 'skipped' &&
      parsed?.status !== 'in_progress'
    ) {
      return null
    }
    const isTerminal =
      parsed.status === 'complete' || parsed.status === 'skipped'
    if (isTerminal && !parsed.completedAt) return null
    return parsed
  } catch {
    return null
  }
}

export const readOnboardingPreference = (
  preferences: UserInput['preferences'] | undefined,
): OnboardingPreferencePayload | null => {
  const raw = pref(preferences, MANAGE_ONBOARDING_PREF_KEY)
  return parseOnboardingPreference(raw)
}

export const hasCompletedOnboarding = (
  preferences: UserInput['preferences'] | undefined,
): boolean => {
  if (!preferences?.length) return false
  const payload = readOnboardingPreference(preferences)
  return payload?.status === 'complete' || payload?.status === 'skipped'
}

export const hasStartedOnboarding = (
  preferences: UserInput['preferences'] | undefined,
): boolean => {
  if (!preferences?.length) return false
  return readOnboardingPreference(preferences) !== null
}

export const buildOnboardingPreferencePayload = (params: {
  status: OnboardingStatus
  invitationOrganizationId?: string
  objective?: OnboardingObjective
  startMode?: OnboardingPreferencePayload['startMode']
  collaborationMode?: OnboardingPreferencePayload['collaborationMode']
  defaultSaleStatus?: boolean
  createdEntities?: OnboardingPreferencePayload['createdEntities']
}): OnboardingPreferencePayload => {
  const payload: OnboardingPreferencePayload = {
    status: params.status,
    invitationOrganizationId: params.invitationOrganizationId,
    objective: params.objective,
    startMode: params.startMode,
    collaborationMode: params.collaborationMode,
    defaultSaleStatus: params.defaultSaleStatus,
    createdEntities: params.createdEntities,
  }
  if (params.status === 'complete' || params.status === 'skipped') {
    payload.completedAt = new Date().toISOString()
  }
  return payload
}

export const serializeOnboardingPreference = (
  payload: OnboardingPreferencePayload,
): string => {
  return JSON.stringify(payload)
}
